<template>
  <div class="text-left">
    <h5 class="text-center">
      <strong>{{ $t("name") }}:</strong> {{ title }}
      <span class="ml-5" v-if="supervisor"
        ><strong> Supervisor:</strong> {{ supervisor.username }}</span
      >
    </h5>
    <v-divider />
    <v-card>
      <v-form class="pa-6" ref="form">
        <v-row>
          <v-col cols="12">
            <h3>{{ $t("title.publicationData") }}</h3>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.title") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.title"
              :label="$t('form.title')"
              required
              outlined
              readonly
              :background-color="
                'title' in fieldsChanged ? getFieldsChangedColor : ''
              "
            ></v-text-field>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.abstract") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-textarea
              v-model="publication.abstract"
              :label="$t('form.abstract')"
              required
              outlined
              readonly
              :background-color="'abstract' in fieldsChanged ? 'amber' : ''"
            ></v-textarea>
          </v-col>
        </v-row>
        <h5>{{ $t("form.keywords") }}</h5>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="publication.keywords"
              :label="$t('form.keywords')"
              required
              outlined
              readonly
              :background-color="
                'keywords' in fieldsChanged ? getFieldsChangedColor : ''
              "
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="8" class="py-0">
            <h5>{{ $t("form.authors_text") }}</h5>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="publication.authors_text"
                  :label="$t('form.authors_text')"
                  required
                  outlined
                  readonly
                  :background-color="
                    'authors_text' in fieldsChanged ? getFieldsChangedColor : ''
                  "
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="4" class="py-0">
            <h5>{{ $t("form.num_authors") }}</h5>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="publication.num_authors"
                  :label="$t('form.num_authors')"
                  required
                  readonly
                  outlined
                  type="number"
                  :background-color="
                    'num_authors' in fieldsChanged ? getFieldsChangedColor : ''
                  "
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <h5>{{ $t("form.authors") }}</h5>
        <SelectPeopleComboBox
          :disabled-form="true"
          :items-selected="publication.authorsrel"
          :change-color="'authorsrel' in fieldsChanged"
        />

        <h5>
          {{ $t("form.acknowledgements") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-textarea
              v-model="publication.acknowledgements"
              readonly
              :label="$t('form.acknowledgements')"
              outlined
              :background-color="
                'acknowledgements' in fieldsChanged ? getFieldsChangedColor : ''
              "
            ></v-textarea>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.doi") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.doi"
              :label="$t('form.doi')"
              required
              readonly
              outlined
              :background-color="
                'doi' in fieldsChanged ? getFieldsChangedColor : ''
              "
            ></v-text-field>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.isbn") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.isbn"
              :label="$t('form.isbn')"
              required
              readonly
              outlined
              :background-color="
                'isbn' in fieldsChanged ? getFieldsChangedColor : ''
              "
            ></v-text-field>
          </v-col>
        </v-row>
        <h5>
          {{ $t("form.publication.research_areas") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-select
              :items="researchAreas"
              v-model="publication.research_areas"
              :label="$t('form.publication.research_areas')"
              required
              :item-text="'name.' + $i18n.locale"
              item-value="id"
              outlined
              readonly
              multiple
              :background-color="
                'research_areas' in fieldsChanged ? getFieldsChangedColor : ''
              "
            ></v-select>
          </v-col>
        </v-row>
        <h5>{{ $t("collaborations") }}</h5>
        <v-row>
          <v-col cols="12" md="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.regional"
              :label="$t('form.publication.regional')"
              readonly
              :background-color="
                'regional' in fieldsChanged ? getFieldsChangedColor : ''
              "
            />
          </v-col>
          <v-col cols="12" md="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.national"
              :label="$t('form.publication.national')"
              readonly
              :background-color="
                'national' in fieldsChanged ? getFieldsChangedColor : ''
              "
            />
          </v-col>
          <v-col cols="12" md="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="publication.international"
              :label="$t('form.publication.international')"
              readonly
              :background-color="
                'international' in fieldsChanged ? getFieldsChangedColor : ''
              "
            />
          </v-col>
        </v-row>

        <h5 :class="'links' in fieldsChanged ? 'amber' : ''">
          Links
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.publisher"
              :label="$t('label.links.publisher')"
              outlined
              readonly
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.institutional_repository"
              :label="$t('label.links.institutional_repository')"
              outlined
              readonly
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.personal_repository"
              :label="$t('label.links.personal_repository')"
              outlined
              readonly
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="publication.links.teseo"
              :label="$t('label.links.teseo')"
              outlined
              readonly
            />
          </v-col>
        </v-row>
        <h5 :class="'files' in fieldsChanged ? 'amber' : ''">
          {{ $t("form.files") }}
        </h5>
        <UploadFiles
          :read-only="true"
          object_type="publication"
          :id="Number(this.$route.params.id)"
          :final="false"
        />
        <ReviewModifyArticle
          v-if="publication.article !== null"
          :publication="publication"
          :fields-changed="fieldsChanged"
        />
        <ReviewModifyCommunication
          v-if="publication.communication !== null"
          :publication="publication"
          :fields-changed="fieldsChanged"
        />
        <ReviewModifyThesis
          v-if="publication.thesis !== null"
          :publication="publication"
          :fields-changed="fieldsChanged"
        />
        <ReviewModifyBookAndChapter
          :key="key"
          v-if="publication.bookandchapter !== null"
          :publication="publication"
          :fields-changed="fieldsChanged"
        />
      </v-form>
    </v-card>
    <DirectorActions
      v-if="isAuthorize"
      :object_type="object_type"
      class="mt-6"
    />
    <SupervisorActions
      v-else
      :item="publication"
      :object_type="object_type"
      :form_name="form_name"
      :state="state"
      :supervisor="supervisor !== null ? supervisor.username : ''"
      @change-state-supervisor="onStateSupervisorChange"
      class="mt-6"
    />
  </div>
</template>

<script>
import axios from "axios";
import changeState from "../../../mixins/changeState";
import SelectPeopleComboBox from "../../reusables/SelectPeopleComboBox";
import DirectorActions from "../../action_buttons/DirectorActions";
import SupervisorActions from "../../action_buttons/SupervisorActions";
import ReviewModifyArticle from "./ReviewModifyArticle";
import ReviewModifyCommunication from "./ReviewModifyCommunication";
import ReviewModifyThesis from "./ReviewModifyThesis";
import ReviewModifyBookAndChapter from "./ReviewModifyBookAndChapter";
import UploadFiles from "../../reusables/UploadFiles";
import publication_model from "../../../mixins/model_utils/publication_model";
export default {
  mixins: [changeState, publication_model],
  components: {
    ReviewModifyCommunication,
    ReviewModifyArticle,
    ReviewModifyThesis,
    ReviewModifyBookAndChapter,
    SelectPeopleComboBox,
    SupervisorActions,
    DirectorActions,
    UploadFiles
  },

  data: () => ({
    form_name: "modifyBasicDataForm",
    menu: false,
    key: 0,
    state: "",
    supervisor: "",
    title: "",
    reject_comment: "",
    object_id: null,
    object_type: "publication",
    fieldsChanged: {},
    publication: {
      title: "",
      abstract: "",
      acknowledgements: "",
      authors_text: "",
      links: "",
      authors: [],
      research_areas: [],
      keywords: "",
      isbn: "",
      article: null,
      communication: null,
      bookandchapter: null,
      thesis: null
    }
  }),

  created() {
    this.getIdPublication();
    this.getResearchAreas();
  },
  computed: {
    isAuthorize() {
      return this.$route.meta.authorize !== undefined;
    }
  },

  methods: {
    getIdPublication() {
      let path =
        this.$store.state.endpoint +
        "/moderatedOperations/review/publication/" +
        this.$route.params.id;
      axios
        .get(path)
        .then(response => {
          this.object_id = response.data.object_id;
        })
        .finally(() => this.getPublication())
        .catch(error => {
          console.log(error);
        });
    },
    getPublication() {
      let path = this.$store.state.endpoint + "/publication/" + this.object_id;
      axios
        .get(path)
        .then(response => {
          this.publication = response.data;
        })
        .finally(() => this.getModOperation())
        .catch(error => {
          console.log(error);
        });
    },
    getModOperation() {
      let path =
        this.$store.state.endpoint +
        "/moderatedOperations/review/publication/" +
        this.$route.params.id;
      axios
        .get(path)
        .then(response => {
          this.title = response.data.title;
          this.state = response.data.state;
          this.form_name = response.data.form_name;
          this.reject_comment = response.data.reject_comment;
          this.supervisor = response.data.supervisor;
          this.fieldsChanged = response.data.data.user.publication;
          for (const field in response.data.data.user.publication) {
            if (
              typeof response.data.data.user.publication[field] === "object" &&
              !Array.isArray(response.data.data.user.publication[field])
            ) {
              for (const value in response.data.data.user.publication[field]) {
                if (this.publication[field] === undefined) {
                  this.publication[field] = {};
                }
                this.publication[field][value] =
                  response.data.data.user.publication[field][value];
              }
            } else {
              this.publication[field] =
                response.data.data.user.publication[field];
            }
          }
        })
        .finally(() => (this.key = 1))
        .catch(error => {
          console.log(error);
        });
    },
    onStateSupervisorChange(state, supervisor) {
      this.state = state;
      this.supervisor = supervisor;
    }
  }
};
</script>
